import { useEffect, useState } from "react";
import { api, fmtMoney, today, daysAgo } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ArrowDownToLine, ArrowUpFromLine, Coins } from "lucide-react";
import { cn } from "@/lib/utils";

export default function CashFlow() {
    const [from, setFrom] = useState(daysAgo(180));
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/reports/cash-flow", { params: { date_from: from, date_to: to } }).then(({ data }) => setData(data)); }, [from, to]);

    return (
        <>
            <PageHeader kicker="REPORTS" title="Cash Flow" subtitle="Inflow, outflow, and net cash movement."
                actions={<><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[140px]" /><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[140px]" /></>} />
            <PageBody>
                {data && (
                    <>
                        <div className="grid grid-cols-3 gap-4">
                            <Tile label="Inflow" sub="Money received" value={data.inflow} accent="positive" Icon={ArrowDownToLine} />
                            <Tile label="Outflow" sub="Money out" value={data.outflow} accent="rose" Icon={ArrowUpFromLine} />
                            <Tile label="Net" sub="Period change" value={data.net} accent={data.net >= 0 ? "positive" : "rose"} Icon={Coins} />
                        </div>
                        <div className="bg-card border border-border rounded-md p-5 mt-4">
                            <h3 className="text-[17px] font-semibold mb-4">Monthly trend</h3>
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={data.monthly}>
                                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                                    <YAxis tickFormatter={(v) => fmtMoney(v).replace(".00", "")} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
                                    <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                                    <Line type="monotone" dataKey="inflow" stroke="hsl(var(--positive))" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="outflow" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </>
                )}
            </PageBody>
        </>
    );
}
function Tile({ label, sub, value, accent, Icon }) {
    return (
        <div className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-start justify-between mb-3">
                <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</div>
                {Icon && (
                    <div className={cn("w-9 h-9 rounded-md flex items-center justify-center shrink-0", `tint-${accent}`)}>
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
                    </div>
                )}
            </div>
            <div className="text-[28px] font-semibold tracking-tight kpi-num">{fmtMoney(value)}</div>
            {sub && <div className="text-[11.5px] text-muted-foreground mt-1.5">{sub}</div>}
        </div>
    );
}
